import React, { useState } from 'react';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import useIpLocation from '@src/hooks/useLocation';
import { useTranslation } from 'react-i18next';
import { SUBSCRIPTION_PURCHASE_STATUS } from '@src/constants/subscription';
import { PAYMENT_METHOD_TYPE } from '@src/constants/payment';
import { ORDER_STATUS } from '@src/constants/package';
import { PLATFORM_TYPE } from '@src/constants/support';
import { getPackageName } from '@src/services/package';
import { trackEvent } from '@src/services/moengage';
import { MOE_EVENTS, MOE_AUTO_RENEW_ACTION } from '@src/constants/moengage';
import CommonButton from '@src/new-components/Button';
import IconLink from '@src/assets/images/link.svg';
import theme from '@src/styles/theme';
import actions from '@src/redux/actions';
import apis from '@src/apis';
import ActionDialog from '@src/new-components/Dialog/ActionDialog';
import NotificationDialogV2 from '@src/components/NotificationDialogV2';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { LANDING_PAGE_URL } from '@src/configs';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import CancelAutoRenewDialog from '@src/containers/Subscription/CancelAutoRenewDialog';
import NapasPaymentSourceDialog from '@src/containers/NewPayment/Dialog/NapasPaymentSourceDialog';
import {
  StyledAutoRenewSectionNew,
  StyledHeaderLabel,
  StyledRenewalPlanLabel,
} from './index.style';
import NextTimePayment from '../../AutoRenewPackageInfo/NextTimePayment';
import PaymentMethod from '../../AutoRenewPackageInfo/PaymentMethod';

const AutoRenewSectionNew = ({
  packageInfo,
  packageExpiryDate,
  subscriptionPurchase,
  paymentMethodType,
  isMobile = false,
  orderInfo,
  isInAppPurchase,
}) => {
  const { i18n } = useTranslation();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { t, language } = i18n;
  const { getFeatureValue } = useFeatureFlags();
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const { latestOrder } = useSelector((state) => state.user);
  const { ip } = useIpLocation();
  const [showPackageDetail, setShowPackageDetail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPopupCancelAutoRenew, setShowPopupCancelAutoRenew] =
    useState(false);
  const [showCancelAutoRenewDialog, setShowCancelAutoRenewDialog] =
    useState(false);
  const [napasPaymentSourceDialogOpen, setNapasPaymentSourceDialogOpen] =
    useState(false);

  const { status } = subscriptionPurchase || {};
  const isActive = status === SUBSCRIPTION_PURCHASE_STATUS.ACTIVE;

  const enableCancelAutoRenewPopUpV2 = getFeatureValue(
    FEATURE_KEYS.IMPROVE_AUTO_RENEW_CANCEL_FLOW,
    { ip },
  );

  const enableCancelAutoRenewDialog = getFeatureValue(
    FEATURE_KEYS.CANCEL_AUTO_RENEW_DIALOG,
    { email: user?.email, userId: user?.id },
  );

  // Determine if it's in-app purchase if not explicitly provided
  const isInApp =
    isInAppPurchase !== undefined
      ? isInAppPurchase
      : paymentMethodType === PAYMENT_METHOD_TYPE.APPLE ||
        paymentMethodType === PAYMENT_METHOD_TYPE.GOOGLE;

  const shouldShowCancelAutoRenewText =
    status === SUBSCRIPTION_PURCHASE_STATUS.ACTIVE;

  const checkPendingOrder = (latestOrderData) =>
    latestOrderData && latestOrderData?.status === ORDER_STATUS.PENDING;

  const isNapasCard = paymentMethodType === PAYMENT_METHOD_TYPE.NAPAS_CARD;
  const isNapasAccount =
    paymentMethodType === PAYMENT_METHOD_TYPE.NAPAS_ACCOUNT;
  const isNapasPaymentMethod = isNapasCard || isNapasAccount;

  const fetchSubscriptionPurchase = async () => {
    const data = await apis.subscription.getSubscriptionInfo();
    if (data?.status === 1) {
      dispatch(actions.user.updateSubscriptionPurchase(data?.result || {}));
    }
  };

  const handleCancelSuccess = async ({
    cancelReason,
    cancelReasonOther,
    cancelFeedback,
    setIsSubmitting,
  }) => {
    setIsSubmitting(true);
    const res = await apis.subscription.pauseAutoRenew({
      cancelReason,
      cancelReasonOther: cancelReasonOther || undefined,
      cancelFeedback,
    });
    setIsSubmitting(false);

    if (res?.status !== 1) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'cancelSubscriptionFail',
        }),
      );
      return;
    }

    setShowCancelAutoRenewDialog(false);
    trackEvent(MOE_EVENTS.AUTO_RENEW_CANCELLED, {
      package_code: packageInfo?.code,
      cancel_reason: cancelReason,
      cancel_reason_other: cancelReasonOther,
      cancel_feedback: cancelFeedback,
    });
    dispatch(
      actions.noti.push({
        severity: 'success',
        message: 'cancelSubscriptionSuccess',
      }),
    );
    setTimeout(() => {
      fetchSubscriptionPurchase();
    }, 250);
  };

  const handleRegisterAutoRenew = async (paymentInstrument) => {
    setLoading(true);
    const registerApi = enableCancelAutoRenewDialog
      ? apis.subscription.resumeAutoRenew
      : apis.subscription.registerAutoRenew;
    const response = await registerApi({
      paymentMethodId: orderInfo?.paymentMethod?.id,
      paymentInstrument: isNapasPaymentMethod ? paymentInstrument : undefined,
    });

    const { status: responseStatus, result } = response || {};
    if (responseStatus === 1) {
      trackEvent(MOE_EVENTS.AUTO_RENEW_CLICKED, {
        package_code: packageInfo?.code,
        payment_method_type: paymentMethodType,
        action: enableCancelAutoRenewDialog
          ? MOE_AUTO_RENEW_ACTION.RESUME
          : MOE_AUTO_RENEW_ACTION.REGISTER,
      });
      const { paymentInfo } = result || {};
      const paymentLink = paymentInfo?.link;
      if (paymentLink) window.location.assign(paymentLink);
      setLoading(false);
      if (enableCancelAutoRenewDialog) {
        setTimeout(() => {
          fetchSubscriptionPurchase();
          dispatch(
            actions.noti.push({
              severity: 'success',
              message: 'registerAutoRenewSuccess',
            }),
          );
        }, 250);
      }
      return;
    }

    setLoading(false);
    dispatch(
      actions.noti.push({
        severity: 'error',
        message: 'registerAutoRenewFail',
      }),
    );
  };

  const handleClickButtonAutoRenew = async () => {
    if (shouldShowCancelAutoRenewText) {
      if (enableCancelAutoRenewDialog) {
        setShowCancelAutoRenewDialog(true);
        return;
      }
      setShowPopupCancelAutoRenew(true);
      return;
    }

    if (isNapasPaymentMethod && !enableCancelAutoRenewDialog) {
      setNapasPaymentSourceDialogOpen(true);
      return;
    }

    handleRegisterAutoRenew();
  };

  const handleConfirmNapasPaymentSource = (paymentInstrument) => {
    setNapasPaymentSourceDialogOpen(false);
    handleRegisterAutoRenew(paymentInstrument);
  };

  const handleCancelAutoRenew = async () => {
    setLoading(true);
    const res = await apis.subscription.cancelSubscription();
    if (res?.status !== 1) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'cancelSubscriptionFail',
        }),
      );
    } else {
      trackEvent(MOE_EVENTS.AUTO_RENEW_CANCELLED, {
        package_code: packageInfo?.code,
      });
      dispatch(
        actions.noti.push({
          severity: 'success',
          message: 'cancelSubscriptionSuccess',
        }),
      );

      fetchSubscriptionPurchase();
    }

    setLoading(false);
    setShowPopupCancelAutoRenew(false);
  };

  const handleOpenErrorReport = () => {
    const ticketMetadata = {
      email: user?.email,
      phone: user?.phone,
      cancelAutoRenew: true,
      titleSupport: 'Yêu cầu hỗ trợ hủy tự động gia hạn gói cước',
      userId: user?.id,
    };
    const base64TicketMetadata = btoa(
      encodeURIComponent(JSON.stringify(ticketMetadata)),
    );
    const encodedTicketMetadata = encodeURIComponent(base64TicketMetadata);

    const platform = isMobile
      ? PLATFORM_TYPE.MOBILE_WEB
      : PLATFORM_TYPE.DESKTOP_WEB;

    window.open(
      `${LANDING_PAGE_URL}/support?metadata=${encodedTicketMetadata}&platform=${platform}`,
      '_blank',
    );
  };
  const renderButtonAutoRenewContent = () => {
    if (loading) return <CircularProgress size={24} color="info" />;
    if (shouldShowCancelAutoRenewText) return t('cancelAutoRenew');
    return t('enableAutoRenew');
  };

  const renderActionComponents = () =>
    enableCancelAutoRenewPopUpV2 ? (
      <Box
        className="cancel-auto-renew-buttons-container"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          width: isMobileScreen ? '100%' : '',
          flexDirection: isMobileScreen ? 'column' : 'row',
        }}
      >
        <CommonButton
          className="agree"
          variant="outlined"
          style={{
            height: '40px',
            whiteSpace: 'nowrap',
            borderColor: '#1E164E29',
            color: '#1E164E',
            borderWidth: '1px',
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            width: isMobileScreen ? '100%' : '126px',
          }}
          onClick={handleOpenErrorReport}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={24} color="info" />
          ) : (
            t('cancelAutoRenewButton1')
          )}
        </CommonButton>
        <CommonButton
          className="disagree"
          variant="contained"
          style={{
            whiteSpace: 'nowrap',
            height: '40px',
            borderWidth: '1px',
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            width: isMobileScreen ? '100%' : '158px',
          }}
          onClick={() => setShowPopupCancelAutoRenew(false)}
          disabled={loading}
        >
          {t('cancelAutoRenewButton2')}
        </CommonButton>
      </Box>
    ) : (
      <Box className="cancel-auto-renew-buttons-container">
        <Button
          className="agree"
          onClick={handleCancelAutoRenew}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} color="info" /> : t('agree')}
        </Button>
        <Button
          className="disagree"
          onClick={() => setShowPopupCancelAutoRenew(false)}
          disabled={loading}
        >
          {t('notAgree')}
        </Button>
      </Box>
    );

  if (!packageInfo || !packageExpiryDate) return null;

  return (
    <StyledAutoRenewSectionNew>
      <Box
        className="header-container-auto-renewal"
        onClick={() => setShowPackageDetail(!showPackageDetail)}
      >
        <StyledHeaderLabel isMobile={isMobile}>
          {t('autoRenewal')}
        </StyledHeaderLabel>
        <Box className="header-right">
          <Box className={`status-badge ${isActive ? 'active' : ''}`}>
            <Typography className={`status-text ${isActive ? 'active' : ''}`}>
              {isActive ? t('on') : t('off')}
            </Typography>
          </Box>
          {showPackageDetail ? (
            <ExpandLess className="expand-icon" />
          ) : (
            <ExpandMore className="expand-icon" />
          )}
        </Box>
      </Box>
      {showPackageDetail && (
        <Box className="detail-container">
          <Box className="renewal-plan-row">
            <StyledRenewalPlanLabel isMobile={isMobile}>
              {t('renewalPlan')}
            </StyledRenewalPlanLabel>
            <Typography className="renewal-plan-value">
              {getPackageName(
                packageInfo?.name,
                packageInfo?.code,
                language,
                t,
                true,
              )}
            </Typography>
          </Box>
          <NextTimePayment packageExpiryDate={packageExpiryDate} />
          <PaymentMethod
            paymentMethodType={paymentMethodType}
            paymentMethod={orderInfo?.paymentMethod}
          />
          {!isInApp && (
            <Button
              className="button-subscription"
              onClick={handleClickButtonAutoRenew}
              disabled={loading || checkPendingOrder(latestOrder)}
              data-id="change-auto-renewal-status"
              data-current-package-code={packageInfo?.code}
            >
              {renderButtonAutoRenewContent()}
            </Button>
          )}
        </Box>
      )}
      {showCancelAutoRenewDialog && (
        <CancelAutoRenewDialog
          open={showCancelAutoRenewDialog}
          packageInfo={packageInfo}
          packageExpiryDate={packageExpiryDate}
          onClose={() => setShowCancelAutoRenewDialog(false)}
          onSuccess={handleCancelSuccess}
        />
      )}
      {showPopupCancelAutoRenew &&
        (enableCancelAutoRenewPopUpV2 ? (
          <ActionDialog
            width="512px"
            styleDescription={{ textAlign: 'center' }}
            styleTitle={{ textAlign: 'center' }}
            image={IconLink}
            imageContainerSize="64px"
            title={t('cancelAutoRenewTitle')}
            description={t('cancelAutoRenewDescription')}
            open={showPopupCancelAutoRenew}
            onClose={() => setShowPopupCancelAutoRenew(false)}
            actionComponent={renderActionComponents()}
          />
        ) : (
          <NotificationDialogV2
            showIcon={false}
            title={t('confirmCancelAutoRenewTitle')}
            description={t('confirmCancelAutoRenewDescription')}
            open={showPopupCancelAutoRenew}
            onClose={() => setShowPopupCancelAutoRenew(false)}
            actionComponent={renderActionComponents()}
          />
        ))}
      <NapasPaymentSourceDialog
        open={napasPaymentSourceDialogOpen}
        loading={loading}
        paymentMethodType={paymentMethodType}
        onClose={() => setNapasPaymentSourceDialogOpen(false)}
        onConfirm={handleConfirmNapasPaymentSource}
      />
    </StyledAutoRenewSectionNew>
  );
};

export default AutoRenewSectionNew;
